/**
 * Performance Provider Component
 * Global performance monitoring and optimization provider
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { performanceMonitor, PerformanceMonitor } from '../utils/performance';

interface PerformanceMetrics {
  lcp?: number;
  fid?: number;
  cls?: number;
  ttfb?: number;
  fcp?: number;
  renderTimes: Record<string, number>;
  routeTransitions: Record<string, number>;
  bundleLoadTimes: Record<string, number>;
}

interface PerformanceContextValue {
  metrics: PerformanceMetrics;
  isMonitoring: boolean;
  startMonitoring: () => void;
  stopMonitoring: () => void;
  logSummary: () => void;
  trackComponentRender: (componentName: string) => () => void;
  trackRouteTransition: (routeName: string) => () => void;
}

const PerformanceContext = createContext<PerformanceContextValue | null>(null);

export const usePerformance = (): PerformanceContextValue => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return context;
};

interface PerformanceProviderProps {
  children: ReactNode;
  enabledInProduction?: boolean;
  logInterval?: number;
}

export const PerformanceProvider: React.FC<PerformanceProviderProps> = ({
  children,
  enabledInProduction = false,
  logInterval = 30000 // 30 seconds
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    renderTimes: {},
    routeTransitions: {},
    bundleLoadTimes: {}
  });
  const [isMonitoring, setIsMonitoring] = useState(false);

  const shouldMonitor = import.meta.env.DEV || enabledInProduction;

  useEffect(() => {
    if (!shouldMonitor) return;

    // Start monitoring automatically
    startMonitoring();

    // Periodic metrics update
    const interval = setInterval(() => {
      updateMetrics();
    }, logInterval);

    return () => {
      clearInterval(interval);
      stopMonitoring();
    };
  }, [shouldMonitor, logInterval]);

  const startMonitoring = () => {
    if (!shouldMonitor || isMonitoring) return;

    setIsMonitoring(true);
    performanceMonitor.startCoreWebVitalsMonitoring();
    
    // Monitor resource loading
    if ('PerformanceObserver' in window) {
      try {
        const resourceObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (entry.name.includes('chunk') || entry.name.includes('.js')) {
              const fileName = entry.name.split('/').pop()?.split('?')[0] || entry.name;
              setMetrics(prev => ({
                ...prev,
                bundleLoadTimes: {
                  ...prev.bundleLoadTimes,
                  [fileName]: entry.duration
                }
              }));
            }
          });
        });
        
        resourceObserver.observe({ entryTypes: ['resource'] });
      } catch (e) {
        console.warn('Resource monitoring not supported');
      }
    }

    console.log('🚀 Performance monitoring started');
  };

  const stopMonitoring = () => {
    if (!isMonitoring) return;

    setIsMonitoring(false);
    performanceMonitor.cleanup();
    console.log('⏹️ Performance monitoring stopped');
  };

  const updateMetrics = () => {
    const currentMetrics = performanceMonitor.getMetrics();
    
    setMetrics(prev => ({
      lcp: currentMetrics.lcp || prev.lcp,
      fid: currentMetrics.fid || prev.fid,
      cls: currentMetrics.cls || prev.cls,
      ttfb: currentMetrics.ttfb || prev.ttfb,
      fcp: currentMetrics.fcp || prev.fcp,
      renderTimes: {
        ...prev.renderTimes,
        ...Object.fromEntries(
          Object.entries(currentMetrics).filter(([key]) => key.includes('_render'))
        )
      },
      routeTransitions: {
        ...prev.routeTransitions,
        ...Object.fromEntries(
          Object.entries(currentMetrics).filter(([key]) => key.includes('_transition'))
        )
      },
      bundleLoadTimes: {
        ...prev.bundleLoadTimes,
        ...Object.fromEntries(
          Object.entries(currentMetrics).filter(([key]) => key.includes('lazy_load_'))
        )
      }
    }));
  };

  const logSummary = () => {
    performanceMonitor.logPerformanceSummary();
    
    // Log our custom metrics
    console.group('📊 Custom Performance Metrics');
    console.table({
      'LCP (Largest Contentful Paint)': metrics.lcp ? `${metrics.lcp.toFixed(2)}ms` : 'N/A',
      'FID (First Input Delay)': metrics.fid ? `${metrics.fid.toFixed(2)}ms` : 'N/A',
      'Bundle Load Time': Object.keys(metrics.bundleLoadTimes).length > 0 
        ? `${Object.values(metrics.bundleLoadTimes).reduce((a, b) => a + b, 0).toFixed(2)}ms` 
        : 'N/A',
      'Avg Route Transition': Object.keys(metrics.routeTransitions).length > 0
        ? `${(Object.values(metrics.routeTransitions).reduce((a, b) => a + b, 0) / Object.values(metrics.routeTransitions).length).toFixed(2)}ms`
        : 'N/A'
    });
    console.groupEnd();
  };

  const trackComponentRender = (componentName: string) => {
    return performanceMonitor.measureRender(componentName);
  };

  const trackRouteTransition = (routeName: string) => {
    return performanceMonitor.measureRouteTransition(routeName);
  };

  const contextValue: PerformanceContextValue = {
    metrics,
    isMonitoring,
    startMonitoring,
    stopMonitoring,
    logSummary,
    trackComponentRender,
    trackRouteTransition
  };

  return (
    <PerformanceContext.Provider value={contextValue}>
      {children}
      {shouldMonitor && <PerformanceDevTools />}
    </PerformanceContext.Provider>
  );
};

// Development tools for performance monitoring
const PerformanceDevTools: React.FC = () => {
  const { metrics, logSummary } = usePerformance();
  const [showPanel, setShowPanel] = useState(false);

  useEffect(() => {
    // Add keyboard shortcut to toggle performance panel
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        e.preventDefault();
        setShowPanel(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!showPanel) {
    return (
      <div 
        className="fixed bottom-4 right-4 bg-black text-white px-3 py-2 rounded-lg text-xs cursor-pointer z-50"
        onClick={() => setShowPanel(true)}
        title="Click to open performance panel (Ctrl+Shift+P)"
      >
        📊 Perf
      </div>
    );
  }

  const formatTime = (time?: number) => time ? `${time.toFixed(2)}ms` : 'N/A';

  return (
    <div className="fixed bottom-4 right-4 bg-black text-white p-4 rounded-lg text-xs max-w-sm z-50">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold">Performance Monitor</h3>
        <button 
          onClick={() => setShowPanel(false)}
          className="text-gray-400 hover:text-white"
        >
          ✕
        </button>
      </div>
      
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <div className="text-gray-400">LCP:</div>
            <div className={metrics.lcp && metrics.lcp > 2500 ? 'text-red-400' : 'text-green-400'}>
              {formatTime(metrics.lcp)}
            </div>
          </div>
          <div>
            <div className="text-gray-400">FID:</div>
            <div className={metrics.fid && metrics.fid > 100 ? 'text-red-400' : 'text-green-400'}>
              {formatTime(metrics.fid)}
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-600 pt-2">
          <div className="text-gray-400">Bundle Loads:</div>
          <div className="text-xs">
            {Object.keys(metrics.bundleLoadTimes).length} chunks loaded
          </div>
        </div>
        
        <div className="border-t border-gray-600 pt-2">
          <div className="text-gray-400">Route Transitions:</div>
          <div className="text-xs">
            {Object.keys(metrics.routeTransitions).length} measured
          </div>
        </div>
        
        <button
          onClick={logSummary}
          className="w-full bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded text-white text-xs mt-2"
        >
          Log Full Summary
        </button>
      </div>
    </div>
  );
};

export default PerformanceProvider;